import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useVariant } from '../../contexts/VariantContext';
import { useDomainManager } from '../../config';
import { getDomainImageAuto } from '../../utils/imageManager';
import { fetchBlogList, formatBlogDate, checkBlobStorageAccess } from '../../services/blogService';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const VariantBlog = () => {
  const navigate = useNavigate();
  const { brand, cta, domainConfig } = useTheme();
  const { variant, styles } = useVariant();
  const domainManager = useDomainManager(brand?.id);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('blog');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  // Pagination calculations
  const totalPages = Math.ceil(blogs.length / blogsPerPage);
  const startIndex = (currentPage - 1) * blogsPerPage;
  const endIndex = startIndex + blogsPerPage;
  const currentBlogs = blogs.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Reset to first page when blogs change
  useEffect(() => {
    setCurrentPage(1);
  }, [blogs]);

  // Fetch blogs from Azure Blob Storage only when component mounts
  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch blogs using simplified service
        const blogData = await fetchBlogList();
        setBlogs(blogData);
        
        // If no blogs found, set debug info
        if (blogData.length === 0) {
          const storageCheck = await checkBlobStorageAccess();
          setDebugInfo({
            message: 'No blogs found',
            storageAccess: storageCheck
          });
        }
      } catch (err) {
        console.error('Error loading blogs:', err);
        setError('Failed to load blogs');
        
        // Set debug info on error
        try {
          const storageCheck = await checkBlobStorageAccess();
          setDebugInfo({
            message: 'Error loading blogs',
            error: err.message,
            storageAccess: storageCheck
          });
        } catch (debugErr) {
          setDebugInfo({
            message: 'Error loading blogs',
            error: err.message
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  // Get domain-specific blog content using the unified configuration
  const getBlogContent = () => {
    const blog = domainManager.getBlog();
    if (blog) {
      return {
        title: blog.title,
        subtitle: blog.subtitle,
        description: blog.description,
        posts: blog.posts
      };
    }
    return null;
  };

  // Fallback to legacy system
  const getLegacyBlogContent = () => {
    const domainId = brand?.id;
    
    switch (domainId) {

      default:
        return {
          title: 'Industry Insights',
          subtitle: 'Latest trends and insights in fleet management technology',
          description: 'Stay informed with the latest developments in fleet management, technology, and industry best practices.',
        };
    }
  };

  const { title, subtitle, description } = getBlogContent() || getLegacyBlogContent();

  // Use centralized variant styles
  const variantClasses = {
    container: styles.container,
    hero: styles.heroBg,
    card: styles.card,
    button: styles.button,
    buttonSecondary: styles.buttonSecondary,
    text: styles.text,
    textSecondary: styles.textSecondary,
    accent: styles.accent,
    section: styles.section
  };

  // Handle loading state
  if (loading) {
    return (
      <div className={`pt-16 ${variantClasses.container}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className={`text-lg ${variantClasses.textSecondary}`}>Loading blogs...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className={`pt-16 ${variantClasses.container}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className={`text-3xl font-bold mb-4 ${variantClasses.text}`}>
              {error}
            </h1>
            <p className={`text-lg mb-8 ${variantClasses.textSecondary}`}>
              Please try refreshing the page or contact support if the problem persists.
            </p>
            <button
              onClick={() => window.location.reload()}
              className={`px-6 py-3 font-semibold rounded-lg transition-all duration-300 ${variantClasses.button}`}
            >
              Refresh Page
            </button>
            
            {/* Debug Information */}
            {debugInfo && (
              <div className={`mt-8 p-6 rounded-lg text-left ${variantClasses.card}`}>
                <h3 className={`text-lg font-semibold mb-4 ${variantClasses.accent}`}>
                  Debug Information
                </h3>
                <div className={`text-sm ${variantClasses.textSecondary}`}>
                  <p><strong>Message:</strong> {debugInfo.message}</p>
                  {debugInfo.error && <p><strong>Error:</strong> {debugInfo.error}</p>}
                  {debugInfo.storageAccess && (
                    <div className="mt-2">
                      <p><strong>Storage Access:</strong> {debugInfo.storageAccess.accessible ? 'Accessible' : 'Not Accessible'}</p>
                      {debugInfo.storageAccess.error && <p><strong>Storage Error:</strong> {debugInfo.storageAccess.error}</p>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`pt-16 ${variantClasses.container}`}>
      {/* Hero Section */}
      <div className={`relative overflow-hidden ${variantClasses.hero}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${variantClasses.text}`}>
              {h1Title || title}
            </h1>
            <p className={`text-xl md:text-2xl mb-4 ${variantClasses.textSecondary}`}>
              {subtitle}
            </p>
            <p className={`text-lg max-w-3xl mx-auto ${variantClasses.textSecondary}`}>
              {description}
            </p>
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className={`absolute inset-0 ${
          variant === 'modern' ? 'bg-gradient-to-br from-transparent via-white/5 to-transparent' :
          variant === 'creative' ? 'bg-gradient-to-br from-transparent via-white/10 to-transparent' :
          variant === 'elegant' ? 'bg-gradient-to-br from-transparent via-white/5 to-transparent' :
          'bg-gradient-to-br from-transparent via-white/5 to-transparent'
        }`}></div>
      </div>

      {/* Featured Post - Show first blog of current page */}
      {currentBlogs.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-16">
            <div className={`rounded-2xl shadow-lg overflow-hidden ${
              variantClasses.card
            } ${
              variant === 'creative' ? 'transform hover:rotate-1 transition-transform duration-300' :
              variant === 'elegant' ? 'hover:shadow-xl transition-shadow duration-300' :
              'hover:shadow-xl transition-shadow duration-300'
            }`}>
              <div className="lg:flex">
                <div className="lg:w-1/2 relative h-64 lg:h-96 overflow-hidden">
                  <img
                    src={currentBlogs[0].image}
                    alt={currentBlogs[0].title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/assets/default-fallback.svg';
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 text-xs font-semibold text-white rounded-full ${
                      variant === 'modern' ? 'bg-purple-500' :
                      variant === 'creative' ? 'bg-orange-500' :
                      variant === 'elegant' ? 'bg-slate-600' :
                      variant === 'dark' ? 'bg-blue-500' :
                      'bg-blue-600'
                    }`}>
                      {currentPage === 1 ? 'Latest' : `Page ${currentPage}`}
                    </span>
                  </div>
                </div>
                <div className="lg:w-1/2 p-8 lg:p-12">
                  <div className="flex items-center mb-4">
                    <span className={`text-sm ${variantClasses.textSecondary}`}>
                      {formatBlogDate(currentBlogs[0].date)}
                    </span>
                  </div>
                  <h2 className={`text-2xl lg:text-3xl font-bold mb-4 ${variantClasses.accent}`}>
                    {currentBlogs[0].title}
                  </h2>
                  <p className={`text-lg mb-6 leading-relaxed ${variantClasses.textSecondary}`}>
                    {currentBlogs[0].excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                        variant === 'modern' ? 'bg-purple-500/20' :
                        variant === 'creative' ? 'bg-orange-500/20' :
                        variant === 'elegant' ? 'bg-slate-200' :
                        variant === 'dark' ? 'bg-white/10' :
                        'bg-gray-300'
                      }`}>
                        <span className={`text-sm font-semibold ${
                          variant === 'modern' ? 'text-purple-200' :
                          variant === 'creative' ? 'text-orange-600' :
                          variant === 'elegant' ? 'text-slate-600' :
                          variant === 'dark' ? 'text-white' :
                          'text-gray-600'
                        }`}>
                          {(currentBlogs[0].author || 'FleetHoster').split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className={`text-sm font-semibold ${variantClasses.text}`}>{currentBlogs[0].author || 'FleetHoster'}</p>
                        <p className={`text-sm ${variantClasses.textSecondary}`}>{formatBlogDate(currentBlogs[0].date)}</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => navigate(`/blog/${currentBlogs[0].slug}`)}
                      className={`px-6 py-3 font-semibold rounded-lg transition-all duration-300 ${
                        variantClasses.button
                      }`}
                    >
                      Read More
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blog Posts Grid */}
      {currentBlogs.length > 1 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-2 ${variantClasses.accent}`}>
              Latest Articles
            </h2>
            {totalPages > 1 && (
              <p className={`text-sm ${variantClasses.textSecondary}`}>
                Page {currentPage} of {totalPages} • Showing {currentBlogs.length} of {blogs.length} articles
              </p>
            )}
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {currentBlogs.slice(1).map((blog, index) => (
              <article
                key={blog.id}
                className={`group rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                  variantClasses.card
                } ${
                  variant === 'creative' ? 'hover:scale-105 transform hover:rotate-1' :
                  variant === 'elegant' ? 'hover:scale-105' :
                  'hover:scale-105'
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = '/assets/default-fallback.svg';
                    }}
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <span className={`text-sm ${variantClasses.textSecondary}`}>
                      {formatBlogDate(blog.date)}
                    </span>
                  </div>
                  
                  <h3 className={`text-xl font-bold mb-3 group-hover:opacity-80 transition-opacity duration-300 ${
                    variantClasses.text
                  }`}>
                    {blog.title}
                  </h3>
                  
                  <p className={`mb-4 leading-relaxed ${variantClasses.textSecondary}`}>
                    {blog.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                        variant === 'modern' ? 'bg-purple-500/20' :
                        variant === 'creative' ? 'bg-orange-500/20' :
                        variant === 'elegant' ? 'bg-slate-200' :
                        variant === 'dark' ? 'bg-white/10' :
                        'bg-gray-300'
                      }`}>
                        <span className={`text-xs font-semibold ${
                          variant === 'modern' ? 'text-purple-200' :
                          variant === 'creative' ? 'text-orange-600' :
                          variant === 'elegant' ? 'text-slate-600' :
                          variant === 'dark' ? 'text-white' :
                          'text-gray-600'
                        }`}>
                          {(blog.author || 'FleetHoster').split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <span className={`text-sm ${variantClasses.text}`}>{blog.author || 'FleetHoster'}</span>
                    </div>
                    <button 
                      onClick={() => navigate(`/blog/${blog.slug}`)}
                      className={`text-sm font-semibold hover:opacity-80 transition-opacity duration-300 ${variantClasses.accent}`}
                    >
                      Read More →
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-12 space-x-2">
              {/* Previous Button */}
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  currentPage === 1
                    ? 'opacity-50 cursor-not-allowed'
                    : variantClasses.button
                }`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex space-x-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current page
                  const shouldShow = 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1);
                  
                  if (!shouldShow) {
                    // Show ellipsis for gaps
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} className={`px-3 py-2 ${variantClasses.textSecondary}`}>
                          ...
                        </span>
                      );
                    }
                    return null;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                        page === currentPage
                          ? variantClasses.button
                          : `hover:opacity-80 ${variantClasses.textSecondary} hover:${variantClasses.accent}`
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  currentPage === totalPages
                    ? 'opacity-50 cursor-not-allowed'
                    : variantClasses.button
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* No blogs message */}
      {blogs.length === 0 && !loading && !error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="text-center">
            <h2 className={`text-3xl font-bold mb-4 ${variantClasses.accent}`}>
              No Blogs Available
            </h2>
            <p className={`text-lg mb-8 ${variantClasses.textSecondary}`}>
              Check back soon for the latest insights and updates.
            </p>
            
            {/* Debug Information */}
            {debugInfo && (
              <div className={`mt-8 p-6 rounded-lg text-left max-w-4xl mx-auto ${variantClasses.card}`}>
                <h3 className={`text-lg font-semibold mb-4 ${variantClasses.accent}`}>
                  Debug Information
                </h3>
                <div className={`text-sm ${variantClasses.textSecondary}`}>
                  <p><strong>Message:</strong> {debugInfo.message}</p>
                  {debugInfo.error && <p><strong>Error:</strong> {debugInfo.error}</p>}
                  {debugInfo.storageAccess && (
                    <div className="mt-2">
                      <p><strong>Storage Access:</strong> {debugInfo.storageAccess.accessible ? 'Accessible' : 'Not Accessible'}</p>
                      {debugInfo.storageAccess.error && <p><strong>Storage Error:</strong> {debugInfo.storageAccess.error}</p>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className={`relative overflow-hidden ${
        variant === 'minimal' ? 'bg-gray-50' :
        variant === 'modern' ? 'bg-slate-800/50' :
        variant === 'corporate' ? 'bg-gray-50' :
        variant === 'creative' ? 'bg-white/50' :
        variant === 'elegant' ? 'bg-blue-50' :
        variant === 'landing' ? 'bg-gradient-to-r from-blue-800 to-purple-800' :
        variant === 'dark' ? 'bg-gray-800/50' :
        'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${variantClasses.accent}`}>
              Ready to Get Started?
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${variantClasses.textSecondary}`}>
              Experience our solutions firsthand or explore our products to find the perfect fit for your fleet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
              {renderCtaButton(cta?.primary, 'Request Demo', `w-full sm:w-auto px-8 py-3 font-semibold rounded-lg transition-all duration-300 ${variantClasses.button}`, domainConfig?.shopLink)}
              {renderCtaButton(cta?.secondary, 'Buy Now', `w-full sm:w-auto px-8 py-3 font-semibold rounded-lg transition-all duration-300 text-center ${variantClasses.buttonSecondary || variantClasses.button}`, domainConfig?.shopLink)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantBlog;
